'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';

import { ListingCard } from '@/src/components/ListingCard';
import { SHARE_CLICKSTREAM_CONTEXTS } from '@/src/components/features/listing-actions/listingActionUtils';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import { markSearchHdpHistoryIntent } from '@/src/lib/navigation/searchHdpHistory';
import { buildSrpFilterCarryoverHref } from '@/src/lib/navigation/srpQueryCarryover';
import { useSetActiveListing, useSetMapHoveredMarker, useSetMapPinnedMarker } from '@/src/lib/hooks';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import { useViewportActivation } from '@/src/lib/hooks/ui/useViewportActivation';
import { useViewedListings } from '@/src/lib/hooks/useViewedListings';
import { generateMarkerId } from '@/src/lib/utils/generateMarkerId';
import { getListingHdpHref } from '@/src/lib/utils/listingHref';
import type { SummaryListingDetails } from '@/src/types/summaryListingDetails.type';

type Props = {
  listing: ExtendedListing;
  priority?: boolean;
  /** SSR-safe lg+-scoped priority for cards above the fold only on desktop. */
  desktopPriority?: boolean;
  /** Keep the first visible cards fully interactive in the server response. */
  initiallyInteractive?: boolean;
};

export function ListingsGridItem({ listing, priority, desktopPriority, initiallyInteractive = false }: Props) {
  const isDesktop = useIsDesktop();
  const searchParams = useSearchParams();
  const isMobileMapActive = !isDesktop && searchParams.get('view') === 'map';
  const setHoveredMarker = useSetMapHoveredMarker();
  const setPinnedMarker = useSetMapPinnedMarker();
  const setActiveListing = useSetActiveListing();
  const { markViewed } = useViewedListings();
  const {
    ref: activationRef,
    isActive,
    activate,
  } = useViewportActivation<HTMLLIElement>({
    rootMargin: '600px 0px',
    disabled: initiallyInteractive,
  });
  const interactionMode = initiallyInteractive || isActive ? 'full' : 'navigation-only';

  const markerId = generateMarkerId(listing);
  const hdpHref = useMemo(() => {
    const baseHref = getListingHdpHref(listing) || `/listings/${listing.aliasEncoded}`;
    return buildSrpFilterCarryoverHref(baseHref, searchParams);
  }, [listing, searchParams]);

  useEffect(() => {
    if (!isDesktop) {
      setHoveredMarker(null);
    }
  }, [isDesktop, setHoveredMarker]);

  const handleNavigate = useCallback(
    (clicked: SummaryListingDetails, context?: { searchScrollY?: number }) => {
      const clickedHref = getListingHdpHref(clicked) || `/listings/${clicked.aliasEncoded}`;
      const nextHdpHref = buildSrpFilterCarryoverHref(clickedHref, searchParams);
      markSearchHdpHistoryIntent(nextHdpHref, { searchScrollY: context?.searchScrollY });

      if (isMobileMapActive) {
        setPinnedMarker(null);
        setActiveListing(null);
        markViewed(markerId);
        return;
      }

      setPinnedMarker(generateMarkerId(clicked as ExtendedListing));
      const geoLat = clicked.geo?.lat;
      const geoLng =
        (clicked.geo as { lon?: number; lng?: number } | undefined)?.lon ??
        (clicked.geo as { lon?: number; lng?: number } | undefined)?.lng;
      if (typeof geoLat === 'number' && typeof geoLng === 'number') {
        setActiveListing({
          listingId: generateMarkerId(clicked as ExtendedListing),
          lat: geoLat,
          lng: geoLng,
        });
      }
      markViewed(markerId);
    },
    [isMobileMapActive, markViewed, markerId, searchParams, setActiveListing, setPinnedMarker],
  );

  const handleMouseEnter = useCallback(() => {
    if (!isDesktop) return;
    setHoveredMarker(markerId);
  }, [isDesktop, markerId, setHoveredMarker]);

  const handleMouseLeave = useCallback(() => {
    if (!isDesktop) return;
    setHoveredMarker(null);
  }, [isDesktop, setHoveredMarker]);

  return (
    <li
      ref={activationRef}
      data-srp-listing-card
      data-marker-id={markerId}
      onFocusCapture={activate}
      onPointerEnter={activate}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ListingCard
        listing={listing}
        href={hdpHref}
        priority={priority}
        desktopPriority={desktopPriority}
        shareClickstreamContext={SHARE_CLICKSTREAM_CONTEXTS.srp}
        showContactActions
        interactionMode={interactionMode}
        onNavigate={handleNavigate}
      />
    </li>
  );
}
